import {
  Button,
  CircularProgress,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { ToolType } from "api/resources/aiTools/types"
import Paper from "features/AItools/components/Paper"
import { getToolName } from "features/AItools/helper"
import { Download, SoundLow } from "iconoir-react"
import { useEffect, useState } from "react"

import { NarrationWithLessonPlannerReturnType } from "../../../LessonPlanGenerator/useNarrationWithLessonPlanner"
import { ExportOutputButton } from "../../Export/ContentActions"
import GenerateButton from "../../GenerateButton"
import { generateUuidV4 } from "../../LessonPlanOutput/ProOutput/Content/helper"
import OutputToolbar from "../../OutputToolbar"
import ToolOutput from "../../ToolOutput"

const toolType = ToolType.narration

const TemplateNarrationsList = ({
  narrationIds,
  lessonPlanId,
  lessonPlanLoading,
  awaitLessonPlan,
  generateToolWithLessonPlan,
  onGeneration,
  ...narrationContentProps
}: {
  narrationIds: {
    id: number
    responseIds: number[]
  }[]

  onGeneration: (id: number) => void
  lessonPlanId: number | null
  lessonPlanLoading?: boolean
  awaitLessonPlan: boolean
} & NarrationContentType &
  Pick<NarrationWithLessonPlannerReturnType, "generateToolWithLessonPlan">) => {
  const hasOnlyOneHandout = narrationIds.length === 1

  const generate = async () => {
    if (awaitLessonPlan) {
      toast.info("Please wait while lesson plan generates")
    } else {
      try {
        await generateToolWithLessonPlan.mutateAsync({
          data: {
            lessonPlanResponse: lessonPlanId!,
            generatedWithLessonPlan: true,
            groupId: generateUuidV4(),
          },
          onSuccess: onGeneration,
        })
      } catch {
        // error is handled by the mutation's onError callback
      }
    }
  }
  if (narrationIds.length === 0) {
    return (
      <Paper className="relative grid place-content-center py-5">
        {(lessonPlanId === null || lessonPlanLoading) && (
          <div className="absolute left-0 top-0 grid size-full place-items-center bg-white/50">
            <CircularProgress />
          </div>
        )}
        <div className="flex flex-col items-center gap-2">
          <div className="grid size-15 place-items-center rounded-full bg-success-50 text-success-500">
            <SoundLow className="size-10" strokeWidth={1} />
          </div>
          <Typography variant="title3" className="max-w-[400px] text-center">
            Looks like you haven’t generated any {getToolName(toolType)} for
            this Lesson Plan.
          </Typography>
          <GenerateButton
            onClick={() => {
              generate()
            }}
            loading={false}
            label="Generate"
          />
        </div>
      </Paper>
    )
  }

  return (
    <div className="mb-4 space-y-4">
      {narrationIds.length > 0 &&
        narrationIds.map((responseId, index) => {
          return (
            <div key={responseId.id}>
              <FetchNarrationContent
                {...narrationContentProps}
                sequence={hasOnlyOneHandout ? undefined : index + 1}
                responseIds={responseId.responseIds}
                narrationIds={narrationIds}
              />
            </div>
          )
        })}
    </div>
  )
}

export type NarrationContentType = { resetTempNarration: () => void } & Pick<
  NarrationWithLessonPlannerReturnType,
  | "isLoading"
  | "regenerateResponse"
  | "userVote"
  | "setCurrentResponseId"
  | "output"
  | "finalizedOutputs"
  | "setCurrentResponseId"
>

const FetchNarrationContent = ({
  responseIds,
  sequence,
  finalizedOutputs,
  output,
  regenerateResponse,
  userVote,
  isLoading,
  resetTempNarration,
  narrationIds,
  ...props
}: NarrationContentType & {
  responseIds: number[]
  sequence: number | undefined

  narrationIds: {
    id: number
    responseIds: number[]
  }[]
}) => {
  const [currentResponseId, setState] = useState<number | null>(responseIds[0])
  const setCurrentResponseId = (id: number, t?: { skipCall: boolean }) => {
    setState(id)
    if (!t?.skipCall) {
      props.setCurrentResponseId(id)
    }
  }
  useEffect(() => {
    if (responseIds && responseIds.length > 0 && finalizedOutputs.length > 0) {
      setCurrentResponseId(responseIds[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const outputData = finalizedOutputs.find(
    item => item.id === currentResponseId
  )

  const currentOutput = outputData?.output || output
  const title = sequence
    ? `${getToolName(toolType)} ${sequence + 1}`
    : getToolName(toolType)

  return (
    <div className="mb-4 flex flex-col gap-2">
      <Paper
        className="p-0"
        endSlot={
          isLoading ? undefined : (
            <OutputToolbar
              toolType={ToolType.narration}
              responseIds={responseIds}
              currentResponseId={currentResponseId}
              copyText={currentOutput}
              isPositiveResponse={outputData?.isPositiveResponse}
              onResponseIdChange={responseId => {
                setCurrentResponseId(responseId)
              }}
              onUserReaction={reaction => {
                userVote.mutate({ reaction, id: currentResponseId })
              }}
              onRegenerate={reason => {
                regenerateResponse.mutate({
                  reason,
                  id: currentResponseId,
                  onSuccess(id) {
                    setCurrentResponseId(id, { skipCall: true })
                  },
                })
              }}
            />
          )
        }
      >
        <div>
          {currentResponseId && !isLoading && (
            <div className="ml-auto mr-2 mt-3 w-fit">
              <ExportOutputButton
                currentResponseId={currentResponseId}
                toolType={toolType}
                title={title}
              >
                <Button
                  onClick={e => {
                    e.stopPropagation()
                  }}
                  size="sm"
                  color="black"
                  variant="outlined"
                  startAdornment={<Download />}
                >
                  Download Narration
                </Button>
              </ExportOutputButton>
            </div>
          )}

          <ToolOutput
            className={isLoading ? undefined : "pt-0"}
            isLoading={isLoading}
            onClick={() => {
              if (narrationIds.length === 1) {
                resetTempNarration()
              } else if (responseIds) {
                setCurrentResponseId(
                  responseIds?.length > 1
                    ? responseIds[responseIds?.length - 1]
                    : responseIds[0]
                )
              }
            }}
            showTitle
          >
            {currentOutput}
          </ToolOutput>
        </div>
      </Paper>
    </div>
  )
}

export default TemplateNarrationsList
